import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'

import { shouldShowTariffSkeleton } from 'pages/avia/detail-ticket/components/ticket-tariff/libs/shouldShowTariffSkeleton.ts'

import tarrifTicketStore from 'entities/ticket/models/tarrif-ticket/tarrif-ticket-store'
import {
  InfoTicket,
  InfoTicketList,
  SkeletonTariff,
} from 'entities/ticket/ui/info-ticket'

import { priceShow } from 'shared/lib/priceShow.ts'
import { TicketItem } from 'shared/models/ticket'
import { Button } from 'shared/ui/button'
import { Paper } from 'shared/ui/paper'

import s from './styles.module.scss'

export interface SelectTicket {
  ticket: TicketItem
  tarrifTicket?: TicketItem
}

interface Props {
  ticket?: TicketItem
  isCheckPage?: boolean
  passengerCount?: number
  shouldLoadTariff?: boolean
  onShowTariffInfo?: (ticket: TicketItem) => void
  onSelectTicket?: (selectTicket: SelectTicket) => void
}

export const TicketWithNoTariff = ({
  ticket,
  onSelectTicket,
  onShowTariffInfo,
  passengerCount,
  isCheckPage,
}: Props) => {
  if (!ticket) return null

  const { t } = useTranslation()

  const titleText = isCheckPage ? t('your_tariff') : t('select_tariff')

  return (
    <div className={s.container}>
      <div className={s.titles}>
        <p className={clsx('bold-20', s.title)}>{titleText}</p>
        <p className={clsx('regular-13', s.subtitle)}>
          {t('tickets_page.passengers_count', { count: passengerCount })}
        </p>
      </div>

      <InfoTicket
        ticket={ticket}
        onShowTariffInfo={() => onShowTariffInfo?.(ticket)}
      />

      {onSelectTicket && (
        <Button
          onClick={() => onSelectTicket?.({ ticket })}
          className={s.button}
        >
          <span className="semi-bold-16">
            {t('buy_for', { price: priceShow(ticket?.price?.KGS) })}
          </span>
        </Button>
      )}
    </div>
  )
}

export const TicketWithTariff = observer(
  ({
    ticket,
    onSelectTicket,
    onShowTariffInfo,
    passengerCount,
    shouldLoadTariff = true,
  }: Props) => {
    if (!ticket) return null

    const { t } = useTranslation()

    const [selectedTicket, setSelectedTicket] = useState<TicketItem | null>(
      null,
    )

    const onSelectTicketHandler = (ticket: TicketItem) => {
      setSelectedTicket(() => ticket)
    }

    const onContinueSelectedTicket = () => {
      if (selectedTicket) {
        onSelectTicket?.({ ticket, tarrifTicket: selectedTicket })
      }
    }

    const ticketsTarriffInfo = useMemo(
      () => toJS(tarrifTicketStore.tariffTicket[ticket.tid]) || null,
      [tarrifTicketStore.tariffTicket[ticket.tid]],
    )

    const showSkeleton = shouldShowTariffSkeleton({
      hasFareFamily: ticket.fare_family_flag,
      tariffInfo: ticketsTarriffInfo,
      shouldLoadTariff,
    })

    useEffect(() => {
      if (!shouldLoadTariff || !ticket?.fare_family_flag) return
      void tarrifTicketStore.getTariffTickets(ticket)
    }, [ticket.tid, shouldLoadTariff])

    useEffect(() => {
      if (ticketsTarriffInfo?.tickets) {
        setSelectedTicket(ticketsTarriffInfo.tickets[0])
      }
    }, [ticketsTarriffInfo?.tickets])

    return (
      <div className={s.container}>
        {!showSkeleton && (
          <InfoTicketList
            selected={selectedTicket?.tid}
            tickets={ticketsTarriffInfo?.tickets}
            onClick={onSelectTicketHandler}
            passengerCount={passengerCount}
            onShowTariffInfo={onShowTariffInfo}
          />
        )}

        {showSkeleton && <SkeletonTariff />}

        {ticketsTarriffInfo?.isError && (
          <Paper
            padding={[16]}
            className={s.errorContainer}
          >
            <p className={clsx('semi-bold-14', s.errorText)}>
              {ticketsTarriffInfo?.error}
            </p>
          </Paper>
        )}

        {!showSkeleton && Boolean(selectedTicket?.price?.KGS) && (
          <Button
            disabled={
              ticketsTarriffInfo?.isLoading ||
              !selectedTicket ||
              ticketsTarriffInfo?.isError
            }
            onClick={onContinueSelectedTicket}
            className={s.button}
          >
            <span className="semi-bold-16">
              {t('buy_for', { price: priceShow(selectedTicket?.price?.KGS) })}
            </span>
          </Button>
        )}
      </div>
    )
  },
)

export const TicketTariff = ({
  ticket,
  shouldLoadTariff = true,
  ...props
}: Props) => {
  if (!ticket) return null

  if (ticket.fare_family_flag)
    return (
      <TicketWithTariff
        ticket={ticket}
        shouldLoadTariff={shouldLoadTariff}
        {...props}
      />
    )

  return (
    <TicketWithNoTariff
      ticket={ticket}
      {...props}
    />
  )
}
